import { getErrorMessage } from "@/utils/error";
import { ApiErrorCode, ApiErrorResponse } from "@common/api/api-types";
import { toast } from "@givenwell/components";
import {
  CancelPurchaseBody,
  CreatePurchaseBody,
  GetPurchaseResponse,
  StorePurchaseReviewRequest,
} from "@givenwell/marketplace-api";
import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { getUserId, meQueryOptions } from "./auth";
import { listingAvailabilityQueryKey } from "./listings";
import { favouritesQuery, mySupportersQuery, myWalletQueryOptions } from "./me";
import { subscriptionsQuery } from "./subscriptions";
import { transactionsQueryKey } from "./transactions";
import { api, queryClient } from "./utils";

export const purchasesListQueryKey = ["purchases", "list"];

export const infinitePurchaseHistoryQueryOptions = (
  userId: string,
  supplierId?: string,
  listingType?: "marketplace" | "eap",
) =>
  infiniteQueryOptions({
    initialPageParam: 1,
    queryKey: [...purchasesListQueryKey, { userId, supplierId, listingType }],
    queryFn: async ({ pageParam }) =>
      (
        await api.purchases.getPurchaseHistory({
          memberId: userId,
          page: pageParam,
          pageSize: 10,
          supplierId,
          listingType: listingType,
        })
      ).purchases,
    enabled: !!userId,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.length < 10) {
        return undefined;
      }

      return pages.length + 1;
    },
  });
export async function prefetchInfinitePurchaseHistory() {
  await queryClient.prefetchQuery(meQueryOptions);
  const userId = getUserId();

  await queryClient.prefetchInfiniteQuery(infinitePurchaseHistoryQueryOptions(userId));
}

export const purchaseQuery = (purchaseId: string) =>
  queryOptions({
    queryKey: ["purchases", "detail", purchaseId],
    queryFn: () => api.purchases.getPurchaseForMember({ purchaseId }),
    enabled: !!purchaseId,
    placeholderData: prev => prev,
  });

// -------------------------------------------------------------------------------------------------

export function useCreatePurchaseMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: CreatePurchaseBody) => {
      return await api.purchases.createPurchase({ requestBody: args });
    },
    onSuccess: () => {
      client.invalidateQueries(myWalletQueryOptions);
      client.invalidateQueries({ queryKey: transactionsQueryKey });
      client.invalidateQueries(mySupportersQuery());
      client.removeQueries({ queryKey: listingAvailabilityQueryKey });
      client.removeQueries({ queryKey: purchasesListQueryKey });
      client.removeQueries(subscriptionsQuery);
      client.invalidateQueries(favouritesQuery);
    },
    onError(e) {
      const { title, description } = describePurchaseApiError(e as unknown as ApiErrorResponse);
      toast.error(title, {
        description,
      });
    },
  });
}

function describePurchaseApiError(error: ApiErrorResponse): { title: string; description: string } {
  switch (error.body.code) {
    case ApiErrorCode.TimeSlotNotAvailable:
      return {
        title: "Sorry",
        description: "The time slot you selected is no longer available. Please choose another.",
      };
    default:
      return {
        title: "Purchase failed",
        description: getErrorMessage(error),
      };
  }
}

// -------------------------------------------------------------------------------------------------
// Cancel purchase
// -------------------------------------------------------------------------------------------------

export const cancellationInformationForPurchaseQuery = (purchaseId: string) =>
  queryOptions({
    queryKey: ["purchases", "cancellation-info", purchaseId],
    queryFn: () => api.purchases.getCancellationInformationForPurchase({ purchaseId }),
  });

export function useCancelPurchaseMutation(purchaseId: string) {
  return useMutation({
    mutationFn: async (requestBody: CancelPurchaseBody) => {
      return await api.purchases.cancelPurchase({ purchaseId, requestBody });
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: myWalletQueryOptions.queryKey, exact: true });
      queryClient.invalidateQueries({ queryKey: transactionsQueryKey });
      queryClient.invalidateQueries({
        queryKey: infinitePurchaseHistoryQueryOptions(getUserId()).queryKey,
        exact: true,
      });
      queryClient.invalidateQueries({ queryKey: purchaseQuery(purchaseId).queryKey, exact: true });
    },
  });
}

// -------------------------------------------------------------------------------------------------
// Purchase Reviews
// -------------------------------------------------------------------------------------------------

export const eapPurchasesForReviewQuery = queryOptions({
  queryKey: ["purchases", "reviews", "eap"],
  queryFn: async () => {
    const user = await queryClient.ensureQueryData(meQueryOptions);
    return api.purchaseReviews.getPurchasesForReview({ memberId: user.id, listingType: "eap" });
  },
});

export function useStorePurchaseReviewMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (args: { purchaseId: string; requestBody: StorePurchaseReviewRequest }) => {
      return await api.purchaseReviews.storePurchaseReview(args);
    },
    onSuccess() {
      client.invalidateQueries(eapPurchasesForReviewQuery);
    },
  });
}

// -------------------------------------------------------------------------------------------------
// Utils
// -------------------------------------------------------------------------------------------------

export function isPurchaseConfirmed(purchase: { purchase_status: GetPurchaseResponse["purchase_status"] }): boolean {
  return purchase.purchase_status === "confirmed" || (purchase.purchase_status as string) === "completed";
}
